import { ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";

import { SelectionPill } from "@shared/designSystem/SelectionPill";
import { ErrorText, SmallText, Text } from "@shared/designSystem/Texts";
import designSystemStyles from "@shared/styles/designSystemStyles";
import styles from "@shared/styles/components/SelectedSearchInput.module.css";

export interface SelectedSearchInputProps<T> {
    value: T[];
    results: T[];
    query: string;
    onQueryChange: (query: string) => void;
    onDebouncedQueryChange: (query: string) => void;
    onSelect: (item: T) => void;
    onRemove: (item: T) => void;
    onEnterPress: () => void;
    getItemKey: (item: T) => string;
    getItemLabel: (item: T) => string;
    renderResult?: (item: T, isHighlighted: boolean) => ReactNode;
    placeholder?: string;
    label?: string;
    loading?: boolean;
    disabled?: boolean;
    error?: string;
    maxVisibleResults?: number;
    debounceMs?: number;
    emptyText?: string;
    className?: string;
}

export const SelectedSearchInput = <T,>({
    value,
    results,
    query,
    onQueryChange,
    onDebouncedQueryChange,
    onSelect,
    onRemove,
    onEnterPress,
    getItemKey,
    getItemLabel,
    renderResult,
    placeholder = "...",
    label = "",
    loading = false,
    disabled = false,
    error,
    maxVisibleResults = 4,
    debounceMs = 300,
    emptyText = "No hay resultados para mostrar.".localized(),
    className,
}: SelectedSearchInputProps<T>) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [isFocused, setIsFocused] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    const listboxID = useId();

    const listMaxHeight = `calc(${designSystemStyles.sizeButtonHeight} * ${maxVisibleResults})`;
    const shouldShowEmptyState = isFocused && !disabled && !loading && query.trim().length > 0 && results.length === 0;
    const shouldShowDropdown = isFocused && !disabled && (loading || results.length > 0 || shouldShowEmptyState);

    useEffect(() => {
        const timeoutID = window.setTimeout(() => {
            onDebouncedQueryChange(query);
        }, debounceMs);

        return () => {
            window.clearTimeout(timeoutID);
        };
    }, [debounceMs, onDebouncedQueryChange, query]);

    useEffect(() => {
        if (results.length === 0) {
            setHighlightedIndex(-1);
            return;
        }

        setHighlightedIndex((currentIndex) => {
            if (currentIndex < 0 || currentIndex >= results.length) {
                return 0;
            }

            return currentIndex;
        });
    }, [results]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) {
                setIsFocused(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedPills = useMemo(() => {
        return value.map((item) => ({
            key: getItemKey(item),
            label: getItemLabel(item),
            item,
        }));
    }, [getItemKey, getItemLabel, value]);

    const selectItem = (item: T) => {
        if (disabled) {
            return;
        }

        onSelect(item);
        setHighlightedIndex(-1);
        inputRef.current?.focus();
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (disabled) {
            return;
        }

        if (event.key === "ArrowDown") {
            if (results.length === 0) {
                return;
            }

            event.preventDefault();
            setIsFocused(true);
            setHighlightedIndex((currentIndex) => (currentIndex + 1) % results.length);
            return;
        }

        if (event.key === "ArrowUp") {
            if (results.length === 0) {
                return;
            }

            event.preventDefault();
            setIsFocused(true);
            setHighlightedIndex((currentIndex) => {
                if (currentIndex <= 0) {
                    return results.length - 1;
                }

                return currentIndex - 1;
            });
            return;
        }

        if (event.key === "Escape") {
            event.preventDefault();
            setIsFocused(false);
            return;
        }

        if (event.key === "Enter") {
            event.preventDefault();
            const highlightedResult = highlightedIndex >= 0 ? results[highlightedIndex] : undefined;

            if (shouldShowDropdown && highlightedResult) {
                selectItem(highlightedResult);
                return;
            }

            onEnterPress();
        }
    };

    return (
        <div
            ref={containerRef}
            className={[styles.container, "DSSelectedSearchInput", className].filter(Boolean).join(" ")}
        >
            {label && (
                <SmallText text={label} />
            )}
            <div
                className={[
                    styles.inputSurface,
                    error ? styles.inputSurfaceError : "",
                    disabled ? styles.disabled : "",
                ].filter(Boolean).join(" ")}
                onClick={() => inputRef.current?.focus()}
            >
                {selectedPills.length > 0 && (
                    <div className={styles.pills}>
                        {selectedPills.map((pill) => (
                            <SelectionPill
                                key={pill.key}
                                label={pill.label}
                                onRemove={() => onRemove(pill.item)}
                                disabled={disabled}
                            />
                        ))}
                    </div>
                )}
                <input
                    ref={inputRef}
                    className={styles.input}
                    value={query}
                    placeholder={placeholder}
                    onChange={(event) => onQueryChange(event.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    role="combobox"
                    aria-expanded={shouldShowDropdown}
                    aria-controls={listboxID}
                    aria-haspopup="listbox"
                    aria-autocomplete="list"
                />
            </div>
            {error && (
                <ErrorText text={error} />
            )}
            {shouldShowDropdown && (
                <div
                    id={listboxID}
                    role="listbox"
                    className={styles.dropdown}
                    onMouseDown={(event) => event.preventDefault()}
                >
                    <div className={styles.list} style={{ maxHeight: listMaxHeight }}>
                        {loading ? (
                            <div className={styles.stateRow}>
                                <Text text={"Buscando...".localized()} color={designSystemStyles.colorTextSecondary} />
                            </div>
                        ) : shouldShowEmptyState ? (
                            <div className={styles.stateRow}>
                                <Text text={emptyText} color={designSystemStyles.colorTextSecondary} />
                            </div>
                        ) : (
                            results.map((item, index) => {
                                const isHighlighted = index === highlightedIndex;
                                return (
                                    <button
                                        key={getItemKey(item)}
                                        type="button"
                                        role="option"
                                        aria-selected={isHighlighted}
                                        className={[styles.option, isHighlighted ? styles.highlighted : ""].filter(Boolean).join(" ")}
                                        onClick={() => selectItem(item)}
                                        onMouseEnter={() => setHighlightedIndex(index)}
                                    >
                                        {renderResult ? (
                                            renderResult(item, isHighlighted)
                                        ) : (
                                            <Text text={getItemLabel(item)} truncate={true} />
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
